import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { useIntl } from "react-intl";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./Button";
import { buttonClasses } from "./button-classes";
import { TextInput } from "./TextField";
import { textFieldClasses } from "./text-field-classes";
import { localToday, parseCalendarDate, shiftCalendarDate, shiftCalendarMonth, validPickerValue } from "./date-picker-value";

export interface DatePickerProps {
  value: string | null | undefined;
  onChange: (next: string) => void;
  mode?: "date" | "datetime-local";
  id?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
  disabled?: boolean;
  required?: boolean;
  min?: string;
  max?: string;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  triggerVariant?: "field" | "icon";
}

export function DatePicker({ value, onChange, mode = "date", id, "aria-label": label, "aria-describedby": describedBy,
  disabled = false, required = false, min, max, placeholder, className = "", autoFocus, triggerVariant = "field" }: DatePickerProps) {
  const intl = useIntl();
  const generatedId = useId();
  const pickerId = id ?? generatedId;
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const focusDay = useRef(false);
  const [open, setOpen] = useState(false);
  const [portalTarget, setPortalTarget] = useState<Element | null>(null);
  const [draft, setDraft] = useState("");
  const [activeDate, setActiveDate] = useState(localToday);
  const [hours, setHours] = useState("09");
  const [minutes, setMinutes] = useState("00");
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const today = localToday();
  const dateTime = mode === "datetime-local";
  const name = label ?? intl.formatMessage(dateTime
    ? { id: "datePicker.chooseDateTime", defaultMessage: "Choose date and time" }
    : { id: "datePicker.chooseDate", defaultMessage: "Choose date" });
  const minDate = min && parseCalendarDate(min.slice(0, 10)) ? min.slice(0, 10) : "0001-01-01";
  const maxDate = max && parseCalendarDate(max.slice(0, 10)) ? max.slice(0, 10) : "9999-12-31";
  const allowedDay = (day: string) => day >= minDate && day <= maxDate;
  const clampDay = (day: string) => day.startsWith("-") || day < minDate ? minDate : day.length > 10 || day > maxDate ? maxDate : day;
  const close = (restore = true) => {
    setOpen(false);
    if (restore) trigger.current?.focus();
  };
  const show = () => {
    setPortalTarget(trigger.current?.closest('[role="dialog"]') ?? document.body);
    const selected = value && validPickerValue(value, mode) ? value : "";
    setDraft(selected.slice(0, 10));
    setActiveDate(clampDay(selected.slice(0, 10) || today));
    setHours(selected.slice(11, 13) || "09");
    setMinutes(selected.slice(14, 16) || "00");
    focusDay.current = true;
    setOpen(true);
  };
  const commit = (next: string) => { onChange(next); close(); };
  const pickDay = (day: string) => {
    focusDay.current = true;
    setDraft(day);
    setActiveDate(day);
    if (!dateTime) commit(day);
  };

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const anchor = trigger.current?.getBoundingClientRect();
      const popup = panel.current?.getBoundingClientRect();
      if (!anchor || !popup) return;
      const top = anchor.bottom + 8 + popup.height <= window.innerHeight - 8
        ? anchor.bottom + 8 : Math.max(8, anchor.top - popup.height - 8);
      setPosition({ top, left: Math.max(8, Math.min(anchor.left, window.innerWidth - popup.width - 8)) });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [open]);

  useEffect(() => {
    if (open && focusDay.current) {
      panel.current?.querySelector<HTMLButtonElement>(`[data-day="${activeDate}"]`)?.focus();
      focusDay.current = false;
    }
  }, [open, activeDate]);

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !panel.current?.contains(event.target) && !trigger.current?.contains(event.target)) setOpen(false);
    };
    const focusOutside = (event: FocusEvent) => {
      if (event.target instanceof Node && !panel.current?.contains(event.target) && !trigger.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("focusin", focusOutside);
    return () => { document.removeEventListener("pointerdown", outside); document.removeEventListener("focusin", focusOutside); };
  }, [open]);

  const date = parseCalendarDate(activeDate)!;
  const month = activeDate.slice(0, 7);
  const monthStart = `${month}-01`;
  const weekday = (parseCalendarDate(monthStart)!.getUTCDay() + 6) % 7;
  const days = Array.from({ length: 42 }, (_, index) => shiftCalendarDate(monthStart, index - weekday));
  const monthFormatter = new Intl.DateTimeFormat(intl.locale, { month: "long", timeZone: "UTC" });
  const fullDateFormatter = new Intl.DateTimeFormat(intl.locale, { dateStyle: "full", timeZone: "UTC" });
  const weekdayFormatter = new Intl.DateTimeFormat(intl.locale, { weekday: "short", timeZone: "UTC" });
  const displayed = value && validPickerValue(value, mode)
    ? intl.formatDate(parseCalendarDate(value.slice(0, 10))!, { year: "numeric", month: "2-digit", day: "2-digit", timeZone: "UTC" })
      + (dateTime ? ` · ${value.slice(11, 16)}` : "") : "";
  const timeValue = `${draft}T${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
  const minimum = min?.length === 10 ? `${min}T00:00` : min;
  const maximum = max?.length === 10 ? `${max}T23:59` : max;
  const validTime = /^\d{1,2}$/.test(hours) && /^\d{1,2}$/.test(minutes) && validPickerValue(timeValue, "datetime-local") && (!minimum || timeValue >= minimum) && (!maximum || timeValue <= maximum);
  const navigate = (next: string, focus = false) => {
    focusDay.current = focus;
    setActiveDate(clampDay(next));
  };
  const onDayKey = (event: KeyboardEvent<HTMLButtonElement>, day: string) => {
    let next: string | undefined;
    if (event.key === "ArrowRight") next = shiftCalendarDate(day, 1);
    if (event.key === "ArrowLeft") next = shiftCalendarDate(day, -1);
    if (event.key === "ArrowDown") next = shiftCalendarDate(day, 7);
    if (event.key === "ArrowUp") next = shiftCalendarDate(day, -7);
    if (event.key === "Home") next = shiftCalendarDate(day, -((parseCalendarDate(day)!.getUTCDay() + 6) % 7));
    if (event.key === "End") next = shiftCalendarDate(day, 6 - ((parseCalendarDate(day)!.getUTCDay() + 6) % 7));
    if (event.key === "PageUp") next = shiftCalendarMonth(day, event.shiftKey ? -12 : -1);
    if (event.key === "PageDown") next = shiftCalendarMonth(day, event.shiftKey ? 12 : 1);
    if (next) { event.preventDefault(); navigate(next, true); }
  };
  const previousMonth = shiftCalendarMonth(activeDate, -1);
  const nextMonth = shiftCalendarMonth(activeDate, 1);

  return <span className={`${triggerVariant === "icon" ? "inline-block" : "block"} min-w-0 ${className}`}>
    <button ref={trigger} id={id} type="button" aria-label={name} aria-describedby={describedBy}
      aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? `${pickerId}-calendar` : undefined}
      disabled={disabled} autoFocus={autoFocus} data-value={value ?? ""}
      className={triggerVariant === "icon" ? buttonClasses({ variant: "icon", size: "sm" }) : textFieldClasses({ className: "flex items-center justify-between gap-4 text-left" })}
      onClick={() => open ? close() : show()}
      onKeyDown={(event) => { if (event.key === "ArrowDown") { event.preventDefault(); show(); } }}>
      {triggerVariant === "field" ? <span className={displayed ? "" : "text-ink-muted"}>{displayed || placeholder || name}</span> : null}
      <CalendarDays aria-hidden="true" size={18} className="shrink-0 text-ink-muted" />
    </button>
    {open && !disabled && portalTarget ? createPortal(<div ref={panel} id={`${pickerId}-calendar`} role="dialog" aria-label={name}
      className="fixed z-[var(--z-dropdown)] w-80 max-w-[calc(100vw-1rem)] max-h-[calc(100vh-1rem)] overflow-auto rounded-md bg-surface p-4 shadow-xl ring-1 ring-ink/10"
      style={position}
      onKeyDown={(event) => {
        if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); close(); }
        if (event.key === "Tab") {
          const controls = Array.from(panel.current?.querySelectorAll<HTMLElement>('button:not([disabled]),input:not([disabled]),select:not([disabled])') ?? []).filter((element) => element.tabIndex >= 0);
          const boundary = event.shiftKey ? controls[0] : controls.at(-1);
          if (event.target === boundary) { event.preventDefault(); close(); }
        }
      }}>
      <div className="flex items-center gap-2">
        <Button variant="icon" size="sm" aria-label={intl.formatMessage({ id: "datePicker.previousMonth", defaultMessage: "Previous month" })}
          disabled={!parseCalendarDate(previousMonth) || previousMonth.slice(0, 7) < minDate.slice(0, 7)} onClick={() => navigate(previousMonth)}><ChevronLeft size={16} aria-hidden="true" /></Button>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <select aria-label={intl.formatMessage({ id: "datePicker.month", defaultMessage: "Month" })} value={date.getUTCMonth()}
            className="min-w-0 flex-1 rounded bg-transparent py-2 text-smycles-body text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
            onChange={(event) => navigate(shiftCalendarMonth(activeDate, Number(event.target.value) - date.getUTCMonth()))}>
            {Array.from({ length: 12 }, (_, index) => <option key={index} value={index}>{monthFormatter.format(new Date(Date.UTC(2024, index, 1)))}</option>)}
          </select>
          <input aria-label={intl.formatMessage({ id: "datePicker.year", defaultMessage: "Year" })} type="number" inputMode="numeric"
            min={Number(minDate.slice(0, 4))} max={Number(maxDate.slice(0, 4))} key={date.getUTCFullYear()} defaultValue={date.getUTCFullYear()}
            className="w-16 rounded bg-transparent py-2 text-smycles-body text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
            onBlur={(event) => {
              const year = Number(event.target.value);
              if (Number.isInteger(year) && year >= 1 && year <= 9999) navigate(shiftCalendarMonth(activeDate, (year - date.getUTCFullYear()) * 12));
              else event.target.value = String(date.getUTCFullYear());
            }} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); event.currentTarget.blur(); } }} />
        </div>
        <Button variant="icon" size="sm" aria-label={intl.formatMessage({ id: "datePicker.nextMonth", defaultMessage: "Next month" })}
          disabled={!parseCalendarDate(nextMonth) || nextMonth.slice(0, 7) > maxDate.slice(0, 7)} onClick={() => navigate(nextMonth)}><ChevronRight size={16} aria-hidden="true" /></Button>
      </div>
      <div className="sr-only" aria-live="polite">{intl.formatDate(date, { month: "long", year: "numeric", timeZone: "UTC" })}</div>
      <table role="grid" aria-label={intl.formatDate(date, { month: "long", year: "numeric", timeZone: "UTC" })} className="mt-4 w-full table-fixed border-collapse">
        <thead><tr>{Array.from({ length: 7 }, (_, index) => <th key={index} scope="col" className="pb-2 text-center text-smycles-meta font-normal text-ink-muted">{weekdayFormatter.format(parseCalendarDate(shiftCalendarDate("2024-01-01", index))!)}</th>)}</tr></thead>
        <tbody>{Array.from({ length: 6 }, (_, week) => <tr key={week}>{days.slice(week * 7, week * 7 + 7).map((day) => !parseCalendarDate(day) ? <td key={day} /> : <td key={day} className="p-0 text-center" aria-selected={draft === day}>
          <button type="button" data-day={day} tabIndex={day === activeDate ? 0 : -1} disabled={!allowedDay(day)}
            aria-label={fullDateFormatter.format(parseCalendarDate(day)!)} aria-current={day === today ? "date" : undefined}
            onClick={() => pickDay(day)} onKeyDown={(event) => onDayKey(event, day)}
            className={`aspect-square w-full rounded-full text-smycles-body transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 disabled:opacity-30 ${draft === day ? "bg-ink" : "hover:bg-ink/[0.06]"} ${day.startsWith(month) || draft === day ? "" : "text-ink-muted"} ${day === today && draft !== day ? "ring-1 ring-inset ring-ink/30" : ""}`}>
            {Number(day.slice(8))}
          </button>
        </td>)}</tr>)}</tbody>
      </table>
      {dateTime ? <div className="mt-4 border-t border-ink/10 pt-4">
        <div className="flex items-center gap-2">
          <TextInput aria-label={intl.formatMessage({ id: "datePicker.hours", defaultMessage: "Hours" })} type="number" inputMode="numeric" min={0} max={23} value={hours} onChange={(event) => setHours(event.target.value)} />
          <span aria-hidden="true">:</span>
          <TextInput aria-label={intl.formatMessage({ id: "datePicker.minutes", defaultMessage: "Minutes" })} type="number" inputMode="numeric" min={0} max={59} value={minutes} onChange={(event) => setMinutes(event.target.value)} />
        </div>
        {draft && !validTime ? <p className="mt-2 text-smycles-meta text-ink-muted" role="status">{intl.formatMessage({ id: "datePicker.invalidTime", defaultMessage: "Enter a valid time within the allowed range." })}</p> : null}
      </div> : null}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-ink/10 pt-4">
        <Button variant="ghost" size="sm" disabled={!allowedDay(today)} onClick={() => { navigate(today); pickDay(today); }}>{intl.formatMessage({ id: "datePicker.today", defaultMessage: "Today" })}</Button>
        {!required ? <Button variant="ghost" size="sm" onClick={() => commit("")}>{intl.formatMessage({ id: "datePicker.clear", defaultMessage: "Clear date" })}</Button> : null}
        {dateTime ? <Button size="compact" disabled={!validTime} onClick={() => commit(timeValue)}>{intl.formatMessage({ id: "datePicker.apply", defaultMessage: "Apply" })}</Button> : null}
      </div>
    </div>, portalTarget) : null}
  </span>;
}
